import { useTranslation } from "react-i18next";
import {
  FiBarChart2,
  FiCalendar,
  FiFileText,
  FiSliders,
  FiUser,
  FiUsers,
} from "react-icons/fi";
import { useParams } from "react-router-dom";

import { useAuthStore } from "@/stores/AuthStore";

import { getPagePath, PAGES } from "../config/pages";
import { NavItemType } from "../covaltech-react-ui/Navbar/Navbar";
import { isSupportedLanguage } from "../i18n/routing";
import useEnvironment from "./useEnvironment";
import useNavCounts from "./useNavCounts";

const useNavItems = () => {
  const { environment } = useEnvironment();
  const { isAuthenticated, user } = useAuthStore();
  const { lang } = useParams<{ lang: string }>();
  const { t } = useTranslation();
  const counts = useNavCounts();
  const organisationRole = user?.organisation?.role;
  const canAccessOrganisationSettings =
    organisationRole === "owner" || organisationRole === "admin";

  let navItems: NavItemType[] = [];

  if (isAuthenticated) {
    const mainSection = t("nav.section_main");

    navItems = navItems.concat([
      {
        href: isSupportedLanguage(lang)
          ? getPagePath("events", lang)
          : PAGES.events,
        text: t("nav.events"),
        icon: <FiCalendar />,
        section: mainSection,
        badge: counts.events ?? undefined,
      },
      {
        href: isSupportedLanguage(lang)
          ? getPagePath("volunteers", lang)
          : PAGES.volunteers,
        text: t("nav.volunteers"),
        icon: <FiUsers />,
        section: mainSection,
        badge: counts.volunteers ?? undefined,
      },
      {
        href: isSupportedLanguage(lang)
          ? getPagePath("contracts", lang)
          : PAGES.contracts,
        text: t("nav.contracts"),
        icon: <FiFileText />,
        section: mainSection,
        badge: counts.contracts ?? undefined,
      },
      {
        href: isSupportedLanguage(lang)
          ? getPagePath("finances", lang)
          : PAGES.finances,
        text: t("nav.finances"),
        icon: <FiBarChart2 />,
        section: mainSection,
      },
    ]);

    if (canAccessOrganisationSettings) {
      navItems = navItems.concat([
        {
          href: isSupportedLanguage(lang)
            ? getPagePath("settings", lang)
            : PAGES.settings,
          text: t("nav.settings"),
          icon: <FiSliders />,
          section: t("nav.section_settings"),
        },
      ]);
    }

    // Profile is a personal account page: available to every authenticated
    // user regardless of organisation role.
    navItems = navItems.concat([
      {
        href: isSupportedLanguage(lang)
          ? getPagePath("profile", lang)
          : PAGES.profile,
        text: t("nav.profile"),
        icon: <FiUser />,
        section: t("nav.section_account"),
      },
    ]);
  }

  // Add development only items
  if (environment === "development") {
    navItems = navItems.concat([]);
  }

  return navItems;
};

export default useNavItems;
